import uuid from "react-uuid";
import Swal from "sweetalert2";
import { GET, POST } from "../../../../services/api";
import { i18n } from "../../../../translate/i18n";
import { AggregatorClassification } from "../../AggregadorCategory/aggregadorCategory.type";
import { LojaAgregador } from "../../lojas/type";
import { CreateAgregatorPublicityDto, typeBannerHotel } from "../agregador-publicity.type";




export function validateForm(data: CreateAgregatorPublicityDto) {
    const requiredFields: Array<{ value: unknown; name: string }> = [
        { value: data.image, name: 'image' },
        { value: data.order, name: 'order' },
        { value: data.title, name: 'title' },
        { value: data.type, name: 'type' },
        { value: data.startDate, name: 'startDate' },
        { value: data.endDate, name: 'endDate' },
        { value: data.description, name: 'description' },
        { value: data.status, name: 'status' },
    ]

    if (data.type !== typeBannerHotel.BANNER && !data.externalLink) {
        requiredFields.push({ value: data.aggregatorClassificationId, name: 'aggregatorClassificationId' })
    }

    const hasEmptyRequiredField = requiredFields.some(({ value, name }) => {
        let res = false
        if (typeof value === 'string') res = value.trim() === ''
        if (typeof value === 'number') res = Number.isNaN(value)


        if (res) {
            Swal.fire({
                icon: 'warning',
                title: 'Atenção',
                text: `O campo: ${name} está vazio`
            })
        }

        return res
    })

    if (hasEmptyRequiredField) {
        return false
    }

    const hasExternalLink = typeof data.externalLink === 'string' && data.externalLink.trim() !== ''
    const hasStore = typeof data.pluginSpaceAggregatorId === 'number' && data.pluginSpaceAggregatorId > 0

    if (hasExternalLink === hasStore) {
        Swal.fire({
            icon: 'warning',
            title: 'Atenção',
            text: 'Informe um link externo ou selecione um local.'
        })
        return false
    }

    if (data.startDate && data.endDate) {
        const startDate = new Date(data.startDate).getTime()
        const endDate = new Date(data.endDate).getTime()

        if (!Number.isNaN(startDate) && !Number.isNaN(endDate) && startDate > endDate) {
            return false
        }
    }

    return true
}

interface SaveImageResult {
    success: boolean
    url: string
    error?: string
}



async function saveImage(
    image: string | File,
    name: string,
    retries: number = 3
): Promise<SaveImageResult> {
    for (let attempt = 1; attempt <= retries; attempt++) {
        try {
            // Se já for uma URL, não precisa fazer upload
            if (typeof image === 'string') {
                return {
                    success: true,
                    url: image,
                }
            }

            const formData = new FormData()

            formData.append('file', image)
            formData.append(
                'nameImage',
                `store-${name}-${uuid()}.jpg`
            )
            formData.append('originalSize', 'true')

            const imageRes = await POST('/upload-images/upload-one', formData)

            if (imageRes.success) {
                return {
                    success: true,
                    url: imageRes.image,
                }
            }

            if (attempt === retries) {
                return {
                    success: false,
                    url: '',
                    error: 'Resposta do servidor indicou falha',
                }
            }

        } catch (error) {
            if (attempt === retries) {
                return {
                    success: false,
                    url: '',
                    error: `Falha ao enviar imagem "${name}" após ${retries} tentativas`,
                }
            }

            // Aguarda antes de tentar novamente
            await new Promise(resolve =>
                setTimeout(resolve, 500 * attempt)
            )
        }
    }

    return {
        success: false,
        url: '',
        error: `Falha ao enviar imagem "${name}"`,
    }
}


export async function uploadImagesInForm(
    form: CreateAgregatorPublicityDto
): Promise<{
    success: boolean
    form: CreateAgregatorPublicityDto
    errors: string[]
}> {

    const uploadErrors: string[] = []

    try {
        const formToSend = form

        // ==========================================
        // IMAGEM PRINCIPAL
        // ==========================================

        if (
            formToSend.image &&
            formToSend.image instanceof File
        ) {
            const mainImageResult = await saveImage(
                formToSend.image,
                formToSend.title
            )
            if (!mainImageResult.success) {
                uploadErrors.push(`Imagem principal: ${mainImageResult.error}`)
                formToSend.image = ''
            } else {
                formToSend.image = mainImageResult.url
            }
        }
        // ==========================================
        // RETORNO
        // ==========================================

        return {
            success: uploadErrors.length === 0,
            form: formToSend,
            errors: uploadErrors,
        }

    } catch (error) {

        const errorMessage =
            error instanceof Error
                ? error.message
                : 'Erro inesperado ao realizar upload das imagens'

        return {
            success: false,
            form,
            errors: [
                ...uploadErrors,
                errorMessage,
            ],
        }
    }
}

export async function getAgregadorStores(): Promise<LojaAgregador[]> {
    try {

        const query = `?field=id&order=ASC&page=0&take=5000&search=`

        const request = await GET(`pluginspaceaggregator${query}`)

        return request.data
    } catch (error) {
        return []
    }

}


export async function getAgregadorClassification(): Promise<AggregatorClassification[]> {
    try {
        const currentPage = 0
        const currentPageSize = 5000
        const currentSearch = ''
        const currentSorting = 'ASC'

        const query = `?field=id&order=${currentSorting}&page=${currentPage}&take=${currentPageSize}&search=${currentSearch}`

        const request = await GET(`aggregator-classification/subcategory${query}`)

        return request.data
    } catch (error) {
        return []
    }

}

export async function resizeImage(file: File, targetWidth: number, targetHeight: number): Promise<File> {
    return new Promise((resolve) => {
        const canvas = document.createElement('canvas')
        const ctx = canvas.getContext('2d')!
        const img = new Image()

        img.onload = () => {
            const { naturalWidth: width, naturalHeight: height } = img

            // Calcular o crop centralizado
            const sourceRatio = width / height
            const targetRatio = targetWidth / targetHeight

            let sourceWidth, sourceHeight, sourceX, sourceY

            if (sourceRatio > targetRatio) {
                // Imagem mais larga - cortar nas laterais
                sourceHeight = height
                sourceWidth = height * targetRatio
                sourceX = (width - sourceWidth) / 2
                sourceY = 0
            } else {
                // Imagem mais alta - cortar em cima e embaixo
                sourceWidth = width
                sourceHeight = width / targetRatio
                sourceX = 0
                sourceY = (height - sourceHeight) / 2
            }

            // Configurar canvas
            canvas.width = targetWidth
            canvas.height = targetHeight

            // Desenhar imagem redimensionada e cortada no centro
            ctx.drawImage(
                img,
                sourceX,
                sourceY,
                sourceWidth,
                sourceHeight,
                0,
                0,
                targetWidth,
                targetHeight
            )

            // Converter canvas para blob e depois para File
            canvas.toBlob(
                (blob) => {
                    if (blob) {
                        const resizedFile = new File([blob], file.name, {
                            type: file.type,
                            lastModified: Date.now(),
                        })
                        resolve(resizedFile)
                    }
                },
                file.type,
                0.9
            )
        }

        img.src = URL.createObjectURL(file)
    })
}

export async function validateImage(file: File): Promise<File | null> {
    return new Promise((resolve) => {
        // Verificar tamanho do arquivo (máximo 2MB)
        const maxSizeInBytes = 2 * 1024 * 1024 // 2MB
        if (file.size > maxSizeInBytes) {
            Swal.fire({
                icon: 'warning',
                title: String(i18n.t('hospitality_publicity_create.validation.title')),
                text: String(i18n.t('hospitality_publicity_create.validation.image_size_error')),
            })
            resolve(null)
            return
        }

        // Verificar dimensões da imagem
        const img = new Image()
        img.onload = async () => {
            const width = img.naturalWidth
            const height = img.naturalHeight

            // Dimensões esperadas: aproximadamente 720x600 (tolerância de ±10%)
            const expectedWidth = 720
            const expectedHeight = 600
            const tolerance = 0.1 // 10%

            const minWidth = expectedWidth * (1 - tolerance)
            const maxWidth = expectedWidth * (1 + tolerance)
            const minHeight = expectedHeight * (1 - tolerance)
            const maxHeight = expectedHeight * (1 + tolerance)

            if (width < minWidth || width > maxWidth || height < minHeight || height > maxHeight) {
                // Perguntar se o usuário quer redimensionar
                const result = await Swal.fire({
                    icon: 'question',
                    title: String(i18n.t('hospitality_publicity_create.validation.title')),
                    html: `
                <p>${String(
                        i18n
                            .t('hospitality_publicity_create.validation.image_dimensions_error', {
                                current: `${width}x${height}`,
                                expected: `${expectedWidth}x${expectedHeight}`,
                            })
                            .replace('200x112', `${expectedWidth}x${expectedHeight}`)
                    )}</p>
                <p><strong>Deseja redimensionar a imagem automaticamente?</strong></p>
                <p><small>A imagem será cortada no centro para se ajustar às dimensões necessárias.</small></p>
              `,
                    showCancelButton: true,
                    confirmButtonText: 'Sim, redimensionar',
                    cancelButtonText: 'Não, escolher outra imagem',
                    confirmButtonColor: '#3085d6',
                    cancelButtonColor: '#d33',
                })

                if (result.isConfirmed) {
                    try {
                        const resizedFile = await resizeImage(file, expectedWidth, expectedHeight)
                        resolve(resizedFile)
                    } catch (error) {
                        Swal.fire({
                            icon: 'error',
                            title: 'Erro',
                            text: 'Ocorreu um erro ao redimensionar a imagem. Tente novamente.',
                        })
                        resolve(null)
                    }
                } else {
                    resolve(null)
                }
                return
            }

            resolve(file)
        }

        img.onerror = () => {
            Swal.fire({
                icon: 'warning',
                title: String(i18n.t('hospitality_publicity_create.validation.title')),
                text: String(i18n.t('hospitality_publicity_create.validation.image_invalid')),
            })
            resolve(null)
        }

        img.src = URL.createObjectURL(file)
    })
}

export async function deleteImage(firstStateImage: string | null) {
    try {
        if (firstStateImage) {
            const response = await POST('/upload-images/delete-one', {
                url: firstStateImage,
            })
            console.log('Imagem deletada com sucesso:', response)
        }
    } catch (error) {
        console.error('Erro ao deletar imagem:', error)
    }
}

